import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { SignaturesService } from "../signatures.service";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import {
  PrintCardVariableDefinition,
  VariableInfo,
  VariablePaletteContext,
} from "../signatures.interface";

const PRINT_CARD_ONLY_VARIABLE_NAMES = new Set(["nfcIcon"]);

const PRINT_CARD_VARIABLE_DEFINITIONS: PrintCardVariableDefinition[] = [
  {
    name: "companyName",
    category: "company",
    displayNameKey: "footers.variable.companyName",
    descriptionKey: "footers.variable.companyNameDesc",
    icon: "business",
  },
  {
    name: "companyWebsite",
    category: "company",
    displayNameKey: "footers.variable.companyWebsite",
    descriptionKey: "footers.variable.companyWebsiteDesc",
    icon: "language",
  },
  {
    name: "companyAddress",
    category: "company",
    displayNameKey: "footers.variable.companyAddress",
    descriptionKey: "footers.variable.companyAddressDesc",
    icon: "location_on",
  },
  {
    name: "companyPhone",
    category: "company",
    displayNameKey: "footers.variable.companyPhone",
    descriptionKey: "footers.variable.companyPhoneDesc",
    icon: "phone",
  },
  {
    name: "contactPersonEmail",
    category: "company",
    displayNameKey: "footers.variable.contactPersonEmail",
    descriptionKey: "footers.variable.contactPersonEmailDesc",
    icon: "alternate_email",
  },
  {
    name: "companyImageUrl",
    category: "company",
    displayNameKey: "footers.variable.companyImageUrl",
    descriptionKey: "footers.variable.companyImageUrlDesc",
    icon: "link",
  },
  {
    name: "userName",
    category: "user",
    displayNameKey: "footers.variable.userName",
    descriptionKey: "footers.variable.userNameDesc",
    icon: "person",
  },
  {
    name: "userGivenName",
    category: "user",
    displayNameKey: "footers.variable.userGivenName",
    descriptionKey: "footers.variable.userGivenNameDesc",
    icon: "person_outline",
  },
  {
    name: "userFamilyName",
    category: "user",
    displayNameKey: "footers.variable.userFamilyName",
    descriptionKey: "footers.variable.userFamilyNameDesc",
    icon: "person_outline",
  },
  {
    name: "userEmail",
    category: "user",
    displayNameKey: "footers.variable.userEmail",
    descriptionKey: "footers.variable.userEmailDesc",
    icon: "email",
  },
  {
    name: "jobTitle",
    category: "user",
    displayNameKey: "footers.variable.jobTitle",
    descriptionKey: "footers.variable.jobTitleDesc",
    icon: "work",
  },
  {
    name: "userDepartment",
    category: "user",
    displayNameKey: "footers.variable.userDepartment",
    descriptionKey: "footers.variable.userDepartmentDesc",
    icon: "business_center",
  },
  {
    name: "userAbout",
    category: "user",
    displayNameKey: "footers.variable.userAbout",
    descriptionKey: "footers.variable.userAboutDesc",
    icon: "description",
  },
  {
    name: "formattedPhoneNumber",
    category: "phone",
    displayNameKey: "footers.variable.formattedPhoneNumber",
    descriptionKey: "footers.variable.formattedPhoneNumberDesc",
    icon: "phone",
  },
  {
    name: "rawPhoneNumber",
    category: "phone",
    displayNameKey: "footers.variable.rawPhoneNumber",
    descriptionKey: "footers.variable.rawPhoneNumberDesc",
    icon: "phone",
  },
  {
    name: "labelPhoneNumber",
    category: "phone",
    displayNameKey: "footers.variable.labelPhoneNumber",
    descriptionKey: "footers.variable.labelPhoneNumberDesc",
    icon: "label",
  },
  {
    name: "formattedMobileNumber",
    category: "phone",
    displayNameKey: "footers.variable.formattedMobileNumber",
    descriptionKey: "footers.variable.formattedMobileNumberDesc",
    icon: "smartphone",
  },
  {
    name: "rawMobileNumber",
    category: "phone",
    displayNameKey: "footers.variable.rawMobileNumber",
    descriptionKey: "footers.variable.rawMobileNumberDesc",
    icon: "smartphone",
  },
  {
    name: "labelMobileNumber",
    category: "phone",
    displayNameKey: "footers.variable.labelMobileNumber",
    descriptionKey: "footers.variable.labelMobileNumberDesc",
    icon: "label",
  },
  {
    name: "QRdigitalCardUrl",
    category: "cards",
    displayNameKey: "footers.variable.QRdigitalCardUrl",
    descriptionKey: "footers.variable.QRdigitalCardUrlDesc",
    icon: "qr_code",
  },
  {
    name: "QRdigitalCardLink",
    category: "cards",
    displayNameKey: "footers.variable.QRdigitalCardLink",
    descriptionKey: "footers.variable.QRdigitalCardLinkDesc",
    icon: "link",
  },
  {
    name: "QRdigitalCard",
    category: "cards",
    displayNameKey: "footers.variable.QRdigitalCard",
    descriptionKey: "footers.variable.QRdigitalCardDesc",
    icon: "qr_code",
  },
  {
    name: "nfcIcon",
    category: "cards",
    displayNameKey: "footers.variable.nfcIcon",
    descriptionKey: "footers.variable.nfcIconDesc",
    icon: "nfc",
  },
];

@Component({
  selector: "app-variable-palette",
  templateUrl: "./variable-palette.component.html",
  styleUrls: ["./variable-palette.component.scss"],
  standalone: false,
})
export class VariablePaletteComponent implements OnInit {
  @Input() targetEditor: { insertVariable?: (text: string) => void } | null =
    null;
  @Input() isExternalExpanded = false;
  @Input() context: VariablePaletteContext = "default";
  @Input() additionalPlaceholders: string[] = [];
  @Output() variableInserted = new EventEmitter<string>();

  @ViewChild("paletteContainer") paletteContainer: ElementRef;

  variables: VariableInfo[] = [];
  filteredVariables: VariableInfo[] = [];
  searchTerm = "";
  isExpanded = false;
  activeCategory = "all";
  private branchVariableNames: Set<string> = new Set();

  private readonly defaultCategories = [
    {
      key: "all",
      label: "footers.palette.allVariables",
      icon: "all_inclusive",
    },
    {
      key: "company",
      label: "footers.palette.companyVariables",
      icon: "business",
    },
    { key: "user", label: "footers.palette.userVariables", icon: "person" },
    { key: "phone", label: "footers.palette.phoneVariables", icon: "phone" },
    {
      key: "branch",
      label: "footers.palette.branchVariables",
      icon: "business_center",
    },
    { key: "other", label: "footers.palette.otherVariables", icon: "apps" },
  ];

  private readonly printCardCategories = [
    {
      key: "all",
      label: "printCards.palette.allVariables",
      icon: "all_inclusive",
    },
    {
      key: "company",
      label: "footers.palette.companyVariables",
      icon: "business",
    },
    { key: "user", label: "footers.palette.userVariables", icon: "person" },
    { key: "phone", label: "footers.palette.phoneVariables", icon: "phone" },
    {
      key: "cards",
      label: "printCards.palette.cards",
      icon: "style",
    },
  ];

  constructor(
    private service: SignaturesService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  async ngOnInit() {
    await this.loadVariables();
    this.filterVariables();
  }

  get isPrintCardContext(): boolean {
    return this.context === "print-card";
  }

  get visibleCategories(): Array<{ key: string; label: string; icon: string }> {
    return this.isPrintCardContext
      ? this.printCardCategories
      : this.defaultCategories;
  }

  private async loadVariables() {
    if (this.isPrintCardContext) {
      this.variables = PRINT_CARD_VARIABLE_DEFINITIONS.map((definition) => ({
        name: definition.name,
        displayName: definition.displayNameKey,
        description: definition.descriptionKey,
        category: definition.category,
        icon: definition.icon,
      }));
      return;
    }

    try {
      const response = (await this.service.placeholders().toPromise()) as
        { branchVariables?: string[]; placeholders?: string[] } | string[];

      if (
        response &&
        typeof response === "object" &&
        "branchVariables" in response &&
        Array.isArray(response.branchVariables)
      ) {
        this.branchVariableNames = new Set(response.branchVariables);
      }

      const placeholders =
        (response && typeof response === "object" && "placeholders" in response
          ? response.placeholders
          : response) || [];
      const loadedNames = new Set<string>();
      this.variables = (Array.isArray(placeholders) ? placeholders : [])
        .map((placeholder: string) => {
          const name = placeholder.replace("${", "").replace("}", "");
          loadedNames.add(name);
          return this.createVariableInfo(name);
        })
        .filter(
          (variable) => !PRINT_CARD_ONLY_VARIABLE_NAMES.has(variable.name),
        );
      for (const name of this.additionalPlaceholders) {
        if (
          !loadedNames.has(name) &&
          !PRINT_CARD_ONLY_VARIABLE_NAMES.has(name)
        ) {
          this.variables.push(this.createVariableInfo(name));
        }
      }
    } catch {
      this.toastr.error(
        this.translate.instant(
          "footers.palette.errorLoadingVariables",
        ) as string,
      );
    }
  }

  private createVariableInfo(name: string): VariableInfo {
    return {
      name,
      displayName: this.getDisplayName(name),
      description: this.getDescription(name),
      category: this.getCategory(name),
      icon: this.getIcon(name),
    };
  }

  private getDisplayName(name: string): string {
    const displayNames: { [key: string]: string } = {
      companyName: "footers.variable.companyName",
      companyWebsite: "footers.variable.companyWebsite",
      companyAddress: "footers.variable.companyAddress",
      companyPhone: "footers.variable.companyPhone",
      companyImage: "footers.variable.companyImage",
      companyImageUrl: "footers.variable.companyImageUrl",
      contactPersonEmail: "footers.variable.contactPersonEmail",
      userName: "footers.variable.userName",
      userGivenName: "footers.variable.userGivenName",
      userFamilyName: "footers.variable.userFamilyName",
      userEmail: "footers.variable.userEmail",
      labelUserEmail: "footers.variable.labelUserEmail",
      userThumbnail: "footers.variable.userThumbnail",
      userThumbnailUrl: "footers.variable.userThumbnailUrl",
      userLinks: "footers.variable.userLinks",
      userHandwritten: "footers.variable.userHandwritten",
      userAbout: "footers.variable.userAbout",
      formattedMobileNumber: "footers.variable.formattedMobileNumber",
      rawMobileNumber: "footers.variable.rawMobileNumber",
      labelMobileNumber: "footers.variable.labelMobileNumber",
      formattedPhoneNumber: "footers.variable.formattedPhoneNumber",
      rawPhoneNumber: "footers.variable.rawPhoneNumber",
      labelPhoneNumber: "footers.variable.labelPhoneNumber",
      labelCustom: "footers.variable.labelCustom",
      addonBanner: "footers.variable.addonBanner",
      addonAdditionalBanner: "footers.variable.addonAdditionalBanner",
      addonLink: "footers.variable.addonLink",
      addonDisclaimer: "footers.variable.addonDisclaimer",
      userPlannedAbsence: "footers.variable.userPlannedAbsence",
      QRdigitalCard: "footers.variable.QRdigitalCard",
      QRdigitalCardUrl: "footers.variable.QRdigitalCardUrl",
      QRdigitalCardLink: "footers.variable.QRdigitalCardLink",
      nfcIcon: "footers.variable.nfcIcon",
      jobTitle: "footers.variable.jobTitle",
      calendarLink: "footers.variable.calendarLink",
      userDepartment: "footers.variable.userDepartment",
      phoneNumber: "footers.variable.phoneNumber",
      mobileNumber: "footers.variable.mobileNumber",
      branchName: "branchSettings.variableLabels.branchName",
      branchAddress: "branchSettings.variableLabels.branchAddress",
      branchCity: "branchSettings.variableLabels.branchCity",
      branchPostalCode: "branchSettings.variableLabels.branchPostalCode",
      branchCountry: "branchSettings.variableLabels.branchCountry",
      branchPhone: "branchSettings.variableLabels.branchPhone",
      branchMail: "branchSettings.variableLabels.branchMail",
      userBranchName: "employee.signatureVariables.userBranchName",
      userBranchAddress: "employee.signatureVariables.userBranchAddress",
    };

    if (displayNames[name]) {
      return displayNames[name];
    }

    if (this.branchVariableNames.has(name)) {
      return name;
    }

    if (name.startsWith("branch") || name.startsWith("userBranch")) {
      return `branchSettings.variableLabels.${name}`;
    }

    return name;
  }

  private getDescription(name: string): string {
    const descriptions: { [key: string]: string } = {
      companyName: "footers.variable.companyNameDesc",
      companyWebsite: "footers.variable.companyWebsiteDesc",
      companyAddress: "footers.variable.companyAddressDesc",
      companyPhone: "footers.variable.companyPhoneDesc",
      companyImage: "footers.variable.companyImageDesc",
      companyImageUrl: "footers.variable.companyImageUrlDesc",
      contactPersonEmail: "footers.variable.contactPersonEmailDesc",
      userName: "footers.variable.userNameDesc",
      userGivenName: "footers.variable.userGivenNameDesc",
      userFamilyName: "footers.variable.userFamilyNameDesc",
      userEmail: "footers.variable.userEmailDesc",
      labelUserEmail: "footers.variable.labelUserEmailDesc",
      userThumbnail: "footers.variable.userThumbnailDesc",
      userThumbnailUrl: "footers.variable.userThumbnailUrlDesc",
      userLinks: "footers.variable.userLinksDesc",
      userHandwritten: "footers.variable.userHandwrittenDesc",
      userAbout: "footers.variable.userAboutDesc",
      formattedMobileNumber: "footers.variable.formattedMobileNumberDesc",
      rawMobileNumber: "footers.variable.rawMobileNumberDesc",
      labelMobileNumber: "footers.variable.labelMobileNumberDesc",
      formattedPhoneNumber: "footers.variable.formattedPhoneNumberDesc",
      rawPhoneNumber: "footers.variable.rawPhoneNumberDesc",
      labelPhoneNumber: "footers.variable.labelPhoneNumberDesc",
      labelCustom: "footers.variable.labelCustomDesc",
      addonBanner: "footers.variable.addonBannerDesc",
      addonAdditionalBanner: "footers.variable.addonAdditionalBannerDesc",
      addonLink: "footers.variable.addonLinkDesc",
      addonDisclaimer: "footers.variable.addonDisclaimerDesc",
      userPlannedAbsence: "footers.variable.userPlannedAbsenceDesc",
      QRdigitalCard: "footers.variable.QRdigitalCardDesc",
      nfcIcon: "footers.variable.nfcIconDesc",
      jobTitle: "footers.variable.jobTitleDesc",
      calendarLink: "footers.variable.calendarLinkDesc",
      userDepartment: "footers.variable.userDepartmentDesc",
      phoneNumber: "footers.variable.phoneNumberDesc",
      mobileNumber: "footers.variable.mobileNumberDesc",
      branchName: "branchSettings.variableLabels.branchName",
      branchAddress: "branchSettings.variableLabels.branchAddress",
      branchCity: "branchSettings.variableLabels.branchCity",
      branchPostalCode: "branchSettings.variableLabels.branchPostalCode",
      branchCountry: "branchSettings.variableLabels.branchCountry",
      branchPhone: "branchSettings.variableLabels.branchPhone",
      branchMail: "branchSettings.variableLabels.branchMail",
      userBranchName: "employee.signatureVariables.userBranchName",
      userBranchAddress: "employee.signatureVariables.userBranchAddress",
    };

    if (descriptions[name]) {
      return descriptions[name];
    }

    if (name.startsWith("branch") || name.startsWith("userBranch")) {
      return "footers.variable.defaultVariableDesc";
    }

    return descriptions[name] || "footers.variable.defaultVariableDesc";
  }

  private getCategory(
    name: string,
  ): "company" | "user" | "phone" | "branch" | "other" {
    if (this.branchVariableNames.has(name)) {
      return "branch";
    }
    if (name.startsWith("company") || name.startsWith("contactPerson")) {
      return "company";
    }
    if (name.startsWith("userBranch") || name.startsWith("branch")) {
      return "branch";
    }
    if (
      name.startsWith("user") ||
      name.startsWith("job") ||
      name === "calendarLink" ||
      name.startsWith("labelUser")
    ) {
      return "user";
    }
    if (name.includes("obileNumber") || name.includes("honeNumber")) {
      return "phone";
    }
    return "other";
  }

  private getIcon(name: string): string {
    const icons: { [key: string]: string } = {
      companyName: "business",
      companyWebsite: "language",
      companyAddress: "location_on",
      companyPhone: "phone",
      companyImage: "image",
      companyImageUrl: "link",
      contactPersonEmail: "alternate_email",
      userName: "person",
      userGivenName: "person_outline",
      userFamilyName: "person_outline",
      userEmail: "email",
      labelUserEmail: "label",
      userThumbnail: "account_circle",
      userThumbnailUrl: "account_circle",
      userLinks: "link",
      userHandwritten: "edit",
      userAbout: "description",
      formattedMobileNumber: "smartphone",
      rawMobileNumber: "smartphone",
      labelMobileNumber: "label",
      formattedPhoneNumber: "phone",
      rawPhoneNumber: "phone",
      labelPhoneNumber: "label",
      labelCustom: "label",
      addonBanner: "image",
      addonAdditionalBanner: "image",
      addonLink: "link",
      addonDisclaimer: "description",
      userPlannedAbsence: "event_busy",
      QRdigitalCard: "qr_code",
      QRdigitalCardUrl: "qr_code",
      QRdigitalCardLink: "link",
      nfcIcon: "nfc",
      jobTitle: "work",
      calendarLink: "event",
      userDepartment: "business_center",
      phoneNumber: "phone",
      mobileNumber: "smartphone",
      branchName: "business_center",
      branchAddress: "location_on",
      branchCity: "location_city",
      branchPostalCode: "markunread_mailbox",
      branchCountry: "public",
      branchPhone: "phone",
      branchMail: "email",
      userBranchName: "business_center",
      userBranchAddress: "location_on",
    };

    if (icons[name]) {
      return icons[name];
    }

    if (name.startsWith("branch") || name.startsWith("userBranch")) {
      return "business_center";
    }

    return icons[name] || "code";
  }

  filterVariables() {
    let filtered = this.variables;

    if (this.activeCategory !== "all") {
      filtered = filtered.filter((v) => v.category === this.activeCategory);
    }

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.name.toLowerCase().includes(term) ||
          (this.translate.instant(v.displayName) as string)
            .toLowerCase()
            .includes(term) ||
          (this.translate.instant(v.description) as string)
            .toLowerCase()
            .includes(term),
      );
    }

    this.filteredVariables = filtered;
  }

  onSearchChange() {
    this.filterVariables();
  }

  onCategoryChange(category: string) {
    this.activeCategory = category;
    this.filterVariables();
  }

  onVariableClick(variable: VariableInfo) {
    const variableText = `\${${variable.name}}`;
    if (this.targetEditor) {
      if (this.targetEditor.insertVariable) {
        this.targetEditor.insertVariable(variableText);
        this.variableInserted.emit(variableText);
      } else {
        void navigator.clipboard.writeText(variableText);
        this.toastr.info(
          this.translate.instant(
            "footers.palette.variableCopiedToClipboard",
          ) as string,
        );
      }
    } else {
      void navigator.clipboard.writeText(variableText);
      this.toastr.info(
        this.translate.instant(
          "footers.palette.variableCopiedToClipboard",
        ) as string,
      );
    }
  }

  onVariableDragStart(event: DragEvent, variable: VariableInfo) {
    if (event.dataTransfer) {
      event.dataTransfer.setData("text/plain", `\${${variable.name}}`);
      event.dataTransfer.effectAllowed = "copy";
    }
  }

  getCategoryVariables(category: string): VariableInfo[] {
    if (category === "all") {
      return this.filteredVariables;
    }
    return this.filteredVariables.filter((v) => v.category === category);
  }

  hasBranchVariables(): boolean {
    if (this.isPrintCardContext || this.activeCategory !== "branch") {
      return true;
    }
    return this.getCategoryVariables("branch").length > 0;
  }

  showBranchEmptyState(): boolean {
    return !this.isPrintCardContext && this.activeCategory === "branch";
  }

  navigateToBranchSettings(): void {
    void this.router.navigate(["/settings/departments"]);
  }
}
